import { useMemo, useCallback } from 'react';
import { useAppInstanceRevalidation } from './useAppInstanceRevalidation';
import { buildBannerProps } from '../utils/buildBannerProps';
import { FinalizedBannerProps } from '../types/FinalizedBannerProps';
import { WidgetRootProps } from '../types/WidgetRootProps';
import { useEnvironment, usePanorama } from '@wix/yoshi-flow-editor';
import { useBannerTheme } from './useBannerTheme';
import { useTextAlign } from './useTextAlign';
import { wiredComponents } from '../components/PaymentMethodsBannerWidget/Widget/wired-components';

export const useBannerProps = ({
  platformProps,
  clientProps,
  priceProps,
}: WidgetRootProps): FinalizedBannerProps => {
  const env = useEnvironment();
  const theme = useBannerTheme();
  const textAlign = useTextAlign();
  const panoramaClient = usePanorama();
  const revalidateAppInstance = useAppInstanceRevalidation(platformProps);

  const reportBannerError = useCallback(
    (error: Error, tags: Record<string, string | number | boolean>) => {
      panoramaClient
        .errorMonitor({
          data: { tags: { ...tags, bannerError: true } },
        })
        ?.reportError(error);
    },
    [panoramaClient],
  );

  return useMemo(
    () => ({
      ...buildBannerProps({
        platformProps,
        viewerPlatformProps: {
          locale: env.language,
          isMobile: env.isMobile,
          isSSR: env.isSSR,
        },
        clientProps,
        priceProps,
        onBannerError: reportBannerError,
        revalidateAppInstance,
      }),
      theme,
      textAlign,
      wiredComponents,
    }),
    [
      clientProps,
      platformProps,
      priceProps,
      theme,
      textAlign,
      env,
      revalidateAppInstance,
      reportBannerError,
    ],
  );
};
